import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import type { City } from '../world/city';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Population, TrafficVehicle } from './population';
import { BOAT_SPECS } from '../render/boats';
import { terrainHeight, WATER_LEVEL } from '../world/terrain';
import { NavalPassengers } from './naval-passengers';
import { MILITARY_BALANCE as BALANCE } from './military-balance';

export interface NavalShip {
  owner: string;
  faction: 'friendly' | 'hostile';
  boat: TrafficVehicle;
  route: (THREE.Vector3 & { reverse?: boolean })[];
  pilots?: string[];
  state: 'moored' | 'sailing' | 'blocked' | 'wreck';
  reason: string;
  trips: number;
  blockedSince: number;
}
const WATER_Y = WATER_LEVEL - 0.565;
const angle = (a: number, b: number) => Math.atan2(Math.sin(b - a), Math.cos(b - a));

/** Finite large craft share ordinary vehicle hits, fire, wreck buoyancy and
 * targets. Route validation checks the whole hull against the actual seabed;
 * movement checks live bodies and structures, including the real wheelhouse. */
export class Naval {
  readonly passengers: NavalPassengers;
  ships: NavalShip[] = [];
  time = 0;
  private epoch = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.epoch = physics.life.epoch;
    this.passengers = new NavalPassengers(this);
  }
  clear(): void {
    this.ships = [];
    this.passengers.clear();
    this.time = 0;
    this.epoch = this.physics.life.epoch;
  }
  create(owner: string, faction: 'friendly' | 'hostile', point: Vec): NavalShip | undefined {
    const at = new THREE.Vector3(point.x, WATER_Y, point.z);
    if (!this.waterClear(at, 0)) return;
    if (
      !this.physics.occupiedBoxClear(
        { x: at.x, y: at.y + 3.1, z: at.z },
        { x: 5.75, y: 3.1, z: 18 },
        new THREE.Quaternion(),
        { x: 0, y: 0, z: 0 },
      )
    )
      return;
    const boat = this.population.createCar(
      `naval:${owner}`,
      'boat',
      at,
      this.population.network.nearest(at),
      'landing',
    );
    boat.name = faction === 'friendly' ? 'Garrison naval supply transport' : 'Grey landing transport';
    boat.color = faction === 'friendly' ? 0x9dc58e : 0xffffff;
    boat.controlled = true;
    boat.state = 'sailing';
    boat.speed = BOAT_SPECS.landing.speed;
    boat.protection = faction === 'hostile' ? BALANCE.hostileHealth : 1;
    boat.yaw = 0;
    const ship: NavalShip = {
      owner,
      faction,
      boat,
      route: [],
      state: 'moored',
      reason: 'Original transport at its actual berth',
      trips: 0,
      blockedSince: 0,
    };
    this.ships.push(ship);
    return ship;
  }
  owns(boat: TrafficVehicle): boolean {
    return this.ships.some((s) => s.boat === boat);
  }
  waterClear(p: Vec, yaw: number): boolean {
    if (!this.city.terrain) return false;
    const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
    for (const x of [-5.75, 0, 5.75])
      for (const z of [-18, 0, 18]) {
        const corner = new THREE.Vector3(x, 0, z).applyQuaternion(q).add(new THREE.Vector3(p.x, p.y, p.z)),
          bed = terrainHeight(this.city.terrain, corner.x, corner.z);
        if (bed === undefined || bed > WATER_Y - 0.08) return false;
      }
    return true;
  }
  setRoute(ship: NavalShip, points: (Vec & { reverse?: boolean })[], reason: string): boolean {
    if (ship.boat.state === 'wreck') return false;
    if (!this.hasPilot(ship)) {
      ship.reason = 'Waiting for an original qualified crew member aboard';
      return false;
    }
    if (this.passengers.crossingGangway(ship)) {
      ship.reason = 'Original passengers are still crossing the deck and gangway';
      return false;
    }
    let from = ship.boat.p;
    for (const point of points) {
      const to = new THREE.Vector3(point.x, WATER_Y, point.z),
        length = to.distanceTo(from),
        steps = Math.max(1, Math.ceil(length / 8)),
        yaw = Math.atan2(from.x - to.x, from.z - to.z) + (point.reverse ? Math.PI : 0);
      for (let i = 0; i <= steps; i++)
        if (!this.waterClear(from.clone().lerp(to, i / steps), yaw)) {
          ship.reason = 'Requested course has insufficient real water depth';
          return false;
        }
      from = to;
    }
    ship.route = points.map((p) =>
      Object.assign(new THREE.Vector3(p.x, WATER_Y, p.z), { reverse: p.reverse }),
    );
    ship.state = 'sailing';
    ship.reason = reason;
    return true;
  }
  private hasPilot(ship: NavalShip): boolean {
    return (
      !ship.pilots ||
      ship.pilots.some((id) => {
        const p = this.passengers.people.get(id);
        return p?.ship === ship && p.state === 'aboard' && !p.person.fatal && p.person.state === 'sitting';
      })
    );
  }
  move(boat: TrafficVehicle, dt: number): void {
    const ship = this.ships.find((s) => s.boat === boat);
    if (!ship || this.epoch !== this.physics.life.epoch) return;
    if (boat.state === 'wreck') {
      ship.state = 'wreck';
      ship.route = [];
      return;
    }
    if (!this.hasPilot(ship)) {
      boat.actualSpeed = 0;
      ship.reason = 'Original transport crew unavailable; vessel stopped';
      return;
    }
    const target = ship.route[0];
    if (!target) {
      boat.actualSpeed = 0;
      return;
    }
    const delta = target.clone().sub(boat.p).setY(0),
      distance = delta.length(),
      step = Math.min(distance, boat.speed * dt),
      move = delta.normalize().multiplyScalar(step),
      turn = angle(boat.yaw, Math.atan2(-delta.x, -delta.z) + (target.reverse ? Math.PI : 0)),
      yaw = boat.yaw + Math.max(-dt * 0.45, Math.min(dt * 0.45, turn)),
      q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw),
      next = boat.p.clone().add(move);
    const clear =
      this.waterClear(next, yaw) &&
      this.physics.occupiedBoxClear(
        { x: boat.p.x, y: boat.p.y + 3.1, z: boat.p.z },
        { x: 5.75, y: 3.1, z: 18 },
        q,
        move,
        boat.body,
      );
    if (!clear) {
      if (ship.state !== 'blocked') ship.blockedSince = this.time;
      ship.state = 'blocked';
      boat.actualSpeed = 0;
      boat.blockedBy = 'Live hull obstruction or shallow seabed';
      ship.reason = boat.blockedBy;
      return;
    }
    ship.state = 'sailing';
    boat.blockedBy = '';
    boat.previous.copy(boat.p);
    boat.previousQ.copy(boat.q);
    boat.p.copy(next);
    boat.q.copy(q);
    boat.yaw = yaw;
    boat.actualSpeed = step / dt;
    boat.body.setNextKinematicTranslation(boat.p);
    boat.body.setNextKinematicRotation(q);
    if (distance <= step + 0.01) {
      ship.route.shift();
      if (!ship.route.length) {
        ship.state = 'moored';
        ship.trips++;
        boat.actualSpeed = 0;
        ship.reason = 'Reached the actual supported landing berth';
      }
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    for (const ship of this.ships)
      if (ship.boat.state === 'wreck') {
        ship.state = 'wreck';
        ship.route = [];
      }
    this.passengers.update(dt);
  }
  seat(ship: NavalShip, seat: number): THREE.Vector3 {
    const row = Math.floor(seat / 8),
      column = seat % 8,
      x = column < 4 ? -0.85 - (3 - column) * 1.1 : 0.85 + (column - 4) * 1.1;
    return new THREE.Vector3(x, 0.88, -15 + row * 1.75).applyQuaternion(ship.boat.q).add(ship.boat.p);
  }
  deckPoint(ship: NavalShip, x: number, z: number): THREE.Vector3 {
    return new THREE.Vector3(x, 0.88, z).applyQuaternion(ship.boat.q).add(ship.boat.p);
  }
  deckSupport(ship: NavalShip, point: Vec, rise = 0.08, drop = 0.15): number | undefined {
    // Public foot navigation intentionally excludes kinematic vehicles. An
    // assigned passenger instead checks the real deck of their own carrier.
    if (!ship.boat.body.isValid()) return;
    let height: number | undefined;
    for (let i = 0; i < ship.boat.body.numColliders(); i++) {
      const hit = ship.boat.body
        .collider(i)
        .castRayAndGetNormal(
          new RAPIER.Ray({ x: point.x, y: point.y + rise, z: point.z }, { x: 0, y: -1, z: 0 }),
          rise + drop,
          false,
        );
      if (hit && hit.normal.y > 0.65)
        height = Math.max(height ?? -Infinity, point.y + rise - hit.timeOfImpact);
    }
    return height;
  }
  retire(ship: NavalShip): boolean {
    if (
      ship.boat.state === 'wreck' ||
      this.passengers.crossingGangway(ship) ||
      !this.population.retireVehicle(ship.boat)
    )
      return false;
    this.passengers.forget(ship);
    this.ships = this.ships.filter((s) => s !== ship);
    return true;
  }
  inspect(): unknown {
    return this.ships.map((s) => ({
      id: s.boat.id,
      faction: s.faction,
      state: s.state,
      p: s.boat.p.toArray(),
      trips: s.trips,
      route: s.route.map((p) => p.toArray()),
      reverseLegs: s.route.map((p) => !!p.reverse),
      pilots: s.pilots,
      reason: s.reason,
      passengers: this.passengers.inspect(s),
    }));
  }
}
